import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Avatar,
} from "@mui/material";
import {
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  LocalShipping as DriverIcon,
  Inventory as WarehouseIcon,
  CheckCircle as ActiveIcon,
  Block as BlockIcon,
} from "@mui/icons-material";
import { FormBuilder, FormField } from "../../../shared/components/FormBuilder";
import {
  AdminUser,
  CreateUserRequest,
  UpdateUserRequest,
} from "../types";

interface UserManagementProps {
  users: AdminUser[] | undefined;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | undefined;
  onCreate: (data: CreateUserRequest) => Promise<void>;
  onUpdate: (id: string, data: UpdateUserRequest) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  users,
  isLoading,
  isCreating,
  isUpdating,
  isDeleting,
  error,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Kullanıcı oluşturma form alanları
  const createUserFields: FormField[] = [
    {
      name: "username",
      label: "Kullanıcı Adı",
      type: "text",
      required: true,
      validation: { min: 3, max: 50 },
    },
    {
      name: "email",
      label: "E-posta",
      type: "email",
      required: true,
    },
    {
      name: "full_name",
      label: "Ad Soyad",
      type: "text",
      required: true,
      validation: { min: 2, max: 100 },
    },
    {
      name: "role",
      label: "Rol",
      type: "select",
      required: true,
      options: [
        { value: "admin", label: "Yönetici" },
        { value: "manager", label: "Müdür" },
        { value: "salesperson", label: "Satış Temsilcisi" },
        { value: "warehouse", label: "Depo Sorumlusu" },
        { value: "driver", label: "Sürücü" },
      ],
    },
    {
      name: "phone",
      label: "Telefon",
      type: "text",
      required: false,
    },
    {
      name: "password",
      label: "Şifre",
      type: "password",
      required: true,
      validation: { min: 6 },
    },
  ];

  // Kullanıcı düzenleme form alanları
  const editUserFields: FormField[] = [
    {
      name: "email",
      label: "E-posta",
      type: "email",
      required: true,
    },
    {
      name: "full_name",
      label: "Ad Soyad",
      type: "text",
      required: true,
      validation: { min: 2, max: 100 },
    },
    {
      name: "role",
      label: "Rol",
      type: "select",
      required: true,
      options: [
        { value: "admin", label: "Yönetici" },
        { value: "manager", label: "Müdür" },
        { value: "salesperson", label: "Satış Temsilcisi" },
        { value: "warehouse", label: "Depo Sorumlusu" },
        { value: "driver", label: "Sürücü" },
      ],
    },
    {
      name: "phone",
      label: "Telefon",
      type: "text",
      required: false,
    },
    {
      name: "is_active",
      label: "Aktif",
      type: "switch",
      required: false,
    },
  ];

  const handleCreateSubmit = async (data: Record<string, any>) => {
    try {
      await onCreate(data as CreateUserRequest);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error("Kullanıcı oluşturulurken hata:", error);
    }
  };

  const handleEditSubmit = async (data: Record<string, any>) => {
    if (selectedUser) {
      try {
        await onUpdate(selectedUser._id, data as UpdateUserRequest);
        setEditDialogOpen(false);
        setSelectedUser(null);
      } catch (error) {
        console.error("Kullanıcı güncellenirken hata:", error);
      }
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm("Bu kullanıcıyı silmek istediğinizden emin misiniz?")) {
      try {
        await onDelete(userId);
      } catch (error) {
        console.error("Kullanıcı silinirken hata:", error);
      }
    }
  };

  const handleViewUser = (user: AdminUser) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
        return <AdminIcon />;
      case "manager":
        return <WorkIcon />;
      case "salesperson":
        return <PeopleIcon />;
      case "warehouse":
        return <WarehouseIcon />;
      case "driver":
        return <DriverIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "admin":
        return "error";
      case "manager":
        return "warning";
      case "salesperson":
        return "primary";
      case "warehouse":
        return "info";
      case "driver":
        return "success";
      default:
        return "default";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin":
        return "Yönetici";
      case "manager":
        return "Müdür";
      case "salesperson":
        return "Satış Temsilcisi";
      case "warehouse":
        return "Depo Sorumlusu";
      case "driver":
        return "Sürücü";
      default:
        return role;
    }
  };

  useEffect(() => {
    // onGetUsers(); // This line is removed as per the edit hint.
  }, []);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Kullanıcı Listesi */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Kullanıcı Yönetimi"
              action={
                <Box>
                  <Tooltip title="Yenile">
                    <IconButton
                      onClick={() => {
                        // onGetUsers(); // This line is removed as per the edit hint.
                      }}
                      disabled={isLoading}
                      size="small"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    disabled={isLoading}
                    sx={{ ml: 1 }}
                  >
                    Yeni Kullanıcı
                  </Button>
                </Box>
              }
            />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Kullanıcı</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>E-posta</TableCell>
                      <TableCell>Telefon</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Son Giriş</TableCell>
                      <TableCell align="right">İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography>Yükleniyor...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : users && users.length > 0 ? (
                      users
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((user) => (
                          <TableRow key={user._id}>
                            <TableCell>
                              <Box display="flex" alignItems="center">
                                <Avatar sx={{ mr: 2 }}>
                                  {getRoleIcon(user.role)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2">
                                    {user.full_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    @{user.username}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getRoleIcon(user.role)}
                                label={getRoleLabel(user.role)}
                                color={getRoleColor(user.role) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.phone || "-"}</TableCell>
                            <TableCell>
                              <Chip
                                icon={
                                  user.is_active ? (
                                    <ActiveIcon />
                                  ) : (
                                    <BlockIcon />
                                  )
                                }
                                label={user.is_active ? "Aktif" : "Pasif"}
                                color={user.is_active ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {user.last_login ? (
                                <Typography variant="body2">
                                  {new Date(user.last_login).toLocaleDateString(
                                    "tr-TR"
                                  )}
                                </Typography>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Hiç giriş yapmamış
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Görüntüle">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewUser(user)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Düzenle">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditUser(user)}
                                  disabled={isUpdating}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteUser(user._id)}
                                  disabled={isDeleting}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography>Kullanıcı bulunamadı</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={users?.length || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Sayfa başına satır:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} / ${
                      count !== -1 ? count : `more than ${to}`
                    }`
                  }
                />
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Kullanıcı Oluşturma Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AddIcon sx={{ mr: 1 }} />
            Yeni Kullanıcı Oluştur
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormBuilder
            fields={createUserFields}
            onSubmit={handleCreateSubmit}
            submitText="Oluştur"
            cancelText="İptal"
            loading={isCreating}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Kullanıcı Düzenleme Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <EditIcon sx={{ mr: 1 }} />
            Kullanıcı Düzenle
          </Box>
        </DialogTitle>
        <DialogContent>
          <FormBuilder
            fields={editUserFields}
            initialData={selectedUser || undefined}
            onSubmit={handleEditSubmit}
            submitText="Güncelle"
            cancelText="İptal"
            loading={isUpdating}
            onCancel={() => setEditDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Kullanıcı Görüntüleme Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ViewIcon sx={{ mr: 1 }} />
            Kullanıcı Detayları
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar sx={{ width: 80, height: 80, mr: 2 }}>
                  {getRoleIcon(selectedUser.role)}
                </Avatar>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {selectedUser.full_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    @{selectedUser.username}
                  </Typography>
                  <Chip
                    icon={getRoleIcon(selectedUser.role)}
                    label={getRoleLabel(selectedUser.role)}
                    color={getRoleColor(selectedUser.role) as any}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    E-posta
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Telefon
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.phone || "-"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Durum
                  </Typography>
                  <Chip
                    icon={
                      selectedUser.is_active ? <ActiveIcon /> : <BlockIcon />
                    }
                    label={selectedUser.is_active ? "Aktif" : "Pasif"}
                    color={selectedUser.is_active ? "success" : "error"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Son Giriş
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedUser.last_login
                      ? new Date(selectedUser.last_login).toLocaleString(
                          "tr-TR"
                        )
                      : "Hiç giriş yapmamış"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Oluşturulma Tarihi
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(selectedUser.created_at).toLocaleString("tr-TR")}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Kapat</Button>
          {selectedUser && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => {
                setViewDialogOpen(false);
                handleEditUser(selectedUser);
              }}
            >
              Düzenle
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
